import { useEffect, type RefObject } from 'react';

export type SchedulerRepaintTarget = {
  instance: () => { repaint: () => void };
};

/** Wait for two animation frames so column widths settle before Scheduler geometry is rebuilt. */
export function scheduleAfterLayout(callback: () => void): () => void {
  let innerId = 0;
  const outerId = window.requestAnimationFrame(() => {
    innerId = window.requestAnimationFrame(callback);
  });
  return () => {
    window.cancelAnimationFrame(outerId);
    window.cancelAnimationFrame(innerId);
  };
}

/**
 * Edge leaves stale appointment widths after a window/container resize.
 * Rebuild Scheduler geometry when the host size or calendar view changes.
 */
export function useSchedulerRepaintOnResize<T extends SchedulerRepaintTarget>(
  hostRef: RefObject<HTMLElement | null>,
  schedulerRef: RefObject<T | null>,
  layoutKey?: string | number | boolean
): void {
  useEffect(() => {
    const host = hostRef.current;
    const repaint = () => {
      schedulerRef.current?.instance().repaint();
    };

    let cancelScheduled: (() => void) | null = null;
    const schedule = () => {
      cancelScheduled?.();
      cancelScheduled = scheduleAfterLayout(repaint);
    };

    schedule();

    if (!host || typeof ResizeObserver === 'undefined') {
      return () => {
        cancelScheduled?.();
      };
    }

    const observer = new ResizeObserver(schedule);
    observer.observe(host);
    return () => {
      observer.disconnect();
      cancelScheduled?.();
    };
  }, [hostRef, schedulerRef, layoutKey]);
}
